<template><div><code-well-header>
  <dt-split-button
    omega-tooltip-text="More calling options"
  >
    Place call
    <template #dropdownList>
      <dt-list-item role="menuitem" navigation-type="arrow-keys"> Option 1 </dt-list-item>
      <dt-list-item role="menuitem" navigation-type="arrow-keys"> Option 2 </dt-list-item>
      <dt-list-item role="menuitem" navigation-type="arrow-keys"> Option 3 </dt-list-item>
    </template>
  </dt-split-button>
</code-well-header>
<h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">This dual-functionality allows for surfacing variations of the default action. It conserves space in the interface and reduces cognitive load. While versatile, they should be used judiciously to avoid overwhelming users or cluttering the UI.</p>
<p class="d-docsite--paragraph">In addition to the <RouteLink to="/components/button.html" class="d-docsite--link d-link">Button component's</RouteLink> documentation:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">The default button supports text or icons, while the secondary action is always an icon.</li>
<li class="d-docsite--list-element">The secondary action selected from its menu replaces the primary action.</li>
<li class="d-docsite--list-element">Ensure that primary and secondary actions are clearly labeled to avoid user confusion.</li>
</ul>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Use Split Buttons when you need to offer a default action paired with closely related actions, such as &quot;Save&quot; with secondary actions like &quot;Save as Draft&quot; or “Save all.”</li>
<li class="d-docsite--list-element">Reserve for scenarios where multiple related actions can reasonably be grouped within the same context via the secondary action.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Avoid using if the secondary action is unrelated or distantly related.</li>
<li class="d-docsite--list-element">Refrain from grouping multiple Split Buttons together, as this can lead to a cluttered and confusing interface.</li>
</ul>
</template>
</dialtone-usage>
<h2 id="variants" tabindex="-1" class="d-docsite--header-2">Variants <a class="header-anchor d-link d-docsite--link d-link" href="#variants" aria-hidden="true">#</a></h2>
<h3 id="base" tabindex="-1" class="d-docsite--header-3">Base <a class="header-anchor d-link d-docsite--link d-link" href="#base" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-stack direction="row" gap="400">
      <dt-split-button omega-tooltip-text="More calling options"> Place Call </dt-split-button>
      <dt-split-button importance="outlined" omega-tooltip-text="More calling options"> Place Call </dt-split-button>
      <dt-split-button importance="clear" omega-tooltip-text="More calling options"> Place Call </dt-split-button>
  </dt-stack>
</code-well-header>
<code-example-tabs
htmlCode='
<span class="d-split-btn">
  <button class="base-button__button d-btn d-btn--primary d-split-btn__alpha d-split-btn__alpha--md" type="button">
    <span class="d-btn__label base-button__label"> Place Call </span>
  </button>
  <button class="base-button__button d-btn d-btn--primary d-btn--icon-only d-split-btn__omega d-split-btn__omega--md" type="button">
    <span class="base-button__icon d-btn__icon d-btn__icon--left">...</span>
  </button>
</span>
<span class="d-split-btn">
  <button class="base-button__button d-btn d-btn--outlined d-split-btn__alpha d-split-btn__alpha--md" type="button">
    <span class="d-btn__label base-button__label"> Place Call </span>
  </button>
  <button class="base-button__button d-btn d-btn--outlined d-btn--icon-only d-split-btn__omega d-split-btn__omega--md" type="button">
    <span class="base-button__icon d-btn__icon d-btn__icon--left">...</span>
  </button>
</span>
<span class="d-split-btn">
  <button class="base-button__button d-btn d-split-btn__alpha d-split-btn__alpha--md" type="button">
    <span class="d-btn__label base-button__label"> Place Call </span>
  </button>
  <button class="base-button__button d-btn d-btn--icon-only d-split-btn__omega d-split-btn__omega--md" type="button">
    <span class="base-button__icon d-btn__icon d-btn__icon--left">...</span>
  </button>
</span>
'
vueCode='
<dt-split-button omega-tooltip-text="More calling options"> Place Call </dt-split-button>
<dt-split-button importance="outlined" omega-tooltip-text="More calling options"> Place Call </dt-split-button>
<dt-split-button importance="clear" omega-tooltip-text="More calling options"> Place Call </dt-split-button>
'
showHtmlWarning />
<h3 id="danger" tabindex="-1" class="d-docsite--header-3">Danger <a class="header-anchor d-link d-docsite--link d-link" href="#danger" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-stack direction="row" gap="400">
      <dt-split-button kind="danger" omega-tooltip-text="More calling options"> Place Call </dt-split-button>
      <dt-split-button importance="outlined" kind="danger" omega-tooltip-text="More calling options"> Place Call </dt-split-button>
      <dt-split-button importance="clear" kind="danger" omega-tooltip-text="More calling options"> Place Call </dt-split-button>
  </dt-stack>
</code-well-header>
<code-example-tabs
htmlCode='
<span class="d-split-btn">
  <button class="base-button__button d-btn d-btn--primary d-btn--danger d-split-btn__alpha d-split-btn__alpha--md" type="button">
    <span class="d-btn__label base-button__label"> Place Call </span>
  </button>
  <button class="base-button__button d-btn d-btn--primary d-btn--danger d-btn--icon-only d-split-btn__omega d-split-btn__omega--md" type="button">
    <span class="base-button__icon d-btn__icon d-btn__icon--left">...</span>
  </button>
</span>
<span class="d-split-btn">
  <button class="base-button__button d-btn d-btn--outlined d-btn--danger d-split-btn__alpha d-split-btn__alpha--md" type="button">
    <span class="d-btn__label base-button__label"> Place Call </span>
  </button>
  <button class="base-button__button d-btn d-btn--outlined d-btn--danger d-btn--icon-only d-split-btn__omega d-split-btn__omega--md" type="button">
    <span class="base-button__icon d-btn__icon d-btn__icon--left">...</span>
  </button>
</span>
<span class="d-split-btn">
  <button class="base-button__button d-btn d-btn--danger d-split-btn__alpha d-split-btn__alpha--md" type="button">
    <span class="d-btn__label base-button__label"> Place Call </span>
  </button>
  <button class="base-button__button d-btn d-btn--danger d-btn--icon-only d-split-btn__omega d-split-btn__omega--md" type="button">
    <span class="base-button__icon d-btn__icon d-btn__icon--left">...</span>
  </button>
</span>
'
vueCode='
<dt-split-button kind="danger" omega-tooltip-text="More calling options"> Place Call </dt-split-button>
<dt-split-button importance="outlined" kind="danger" omega-tooltip-text="More calling options"> Place Call </dt-split-button>
<dt-split-button importance="clear" kind="danger" omega-tooltip-text="More calling options"> Place Call </dt-split-button>
'
showHtmlWarning />
<h3 id="positive" tabindex="-1" class="d-docsite--header-3">Positive <a class="header-anchor d-link d-docsite--link d-link" href="#positive" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-stack direction="row" gap="400">
      <dt-split-button kind="positive" omega-tooltip-text="More calling options"> Place Call </dt-split-button>
      <dt-split-button importance="outlined" kind="positive" omega-tooltip-text="More calling options"> Place Call </dt-split-button>
      <dt-split-button importance="clear" kind="positive" omega-tooltip-text="More calling options"> Place Call </dt-split-button>
  </dt-stack>
</code-well-header>
<code-example-tabs
htmlCode='
<span class="d-split-btn">
<button class="base-button__button d-btn d-btn--primary d-btn--positive d-split-btn__alpha d-split-btn__alpha--md" type="button">
<span class="d-btn__label base-button__label"> Place Call </span>
</button>
<button class="base-button__button d-btn d-btn--primary d-btn--positive d-btn--icon-only d-split-btn__omega d-split-btn__omega--md" type="button">
<span class="base-button__icon d-btn__icon d-btn__icon--left">...</span>
</button>
</span>
<span class="d-split-btn">
<button class="base-button__button d-btn d-btn--outlined d-btn--positive d-split-btn__alpha d-split-btn__alpha--md" type="button">
<span class="d-btn__label base-button__label"> Place Call </span>
</button>
<button class="base-button__button d-btn d-btn--outlined d-btn--positive d-btn--icon-only d-split-btn__omega d-split-btn__omega--md" type="button">
<span class="base-button__icon d-btn__icon d-btn__icon--left">...</span>
</button>
</span>
<span class="d-split-btn">
<button class="base-button__button d-btn d-btn--positive d-split-btn__alpha d-split-btn__alpha--md" type="button">
<span class="d-btn__label base-button__label"> Place Call </span>
</button>
<button class="base-button__button d-btn d-btn--positive d-btn--icon-only d-split-btn__omega d-split-btn__omega--md" type="button">
<span class="base-button__icon d-btn__icon d-btn__icon--left">...</span>
</button>
</span>
'
vueCode='
<dt-split-button kind="positive" omega-tooltip-text="More calling options"> Place Call </dt-split-button>
<dt-split-button kind="positive" importance="outlined" omega-tooltip-text="More calling options"> Place Call </dt-split-button>
<dt-split-button kind="positive" importance="clear" omega-tooltip-text="More calling options"> Place Call </dt-split-button>
'
showHtmlWarning />
<h3 id="inverted" tabindex="-1" class="d-docsite--header-3">Inverted <a class="header-anchor d-link d-docsite--link d-link" href="#inverted" aria-hidden="true">#</a></h3>
<code-well-header bgclass="d-bgc-contrast">
  <dt-stack direction="row" gap="400">
      <dt-split-button kind="inverted" omega-tooltip-text="More calling options"> Place Call </dt-split-button>
      <dt-split-button importance="outlined" kind="inverted" omega-tooltip-text="More calling options"> Place Call </dt-split-button>
      <dt-split-button importance="clear" kind="inverted" omega-tooltip-text="More calling options"> Place Call </dt-split-button>
  </dt-stack>
</code-well-header>
<code-example-tabs
htmlCode='
<span class="d-split-btn">
  <button class="base-button__button d-btn d-btn--primary d-btn--inverted d-split-btn__alpha d-split-btn__alpha--md" type="button">
    <span class="d-btn__label base-button__label"> Place Call </span>
  </button>
  <button class="base-button__button d-btn d-btn--primary d-btn--inverted d-btn--icon-only d-split-btn__omega d-split-btn__omega--md" type="button">
    <span class="base-button__icon d-btn__icon d-btn__icon--left">...</span>
  </button>
</span>
<span class="d-split-btn">
  <button class="base-button__button d-btn d-btn--outlined d-btn--inverted d-split-btn__alpha d-split-btn__alpha--md" type="button">
    <span class="d-btn__label base-button__label"> Place Call </span>
  </button>
  <button class="base-button__button d-btn d-btn--outlined d-btn--inverted d-btn--icon-only d-split-btn__omega d-split-btn__omega--md" type="button">
    <span class="base-button__icon d-btn__icon d-btn__icon--left">...</span>
  </button>
</span>
<span class="d-split-btn">
  <button class="base-button__button d-btn d-btn--inverted d-split-btn__alpha d-split-btn__alpha--md" type="button">
    <span class="d-btn__label base-button__label"> Place Call </span>
  </button>
  <button class="base-button__button d-btn d-btn--inverted d-btn--icon-only d-split-btn__omega d-split-btn__omega--md" type="button">
    <span class="base-button__icon d-btn__icon d-btn__icon--left">...</span>
  </button>
</span>
'
vueCode='
<dt-split-button kind="inverted" omega-tooltip-text="More calling options"> Place Call </dt-split-button>
<dt-split-button importance="outlined" kind="inverted" omega-tooltip-text="More calling options"> Place Call </dt-split-button>
<dt-split-button importance="clear" kind="inverted" omega-tooltip-text="More calling options"> Place Call </dt-split-button>
'
showHtmlWarning />
<h3 id="muted" tabindex="-1" class="d-docsite--header-3">Muted <a class="header-anchor d-link d-docsite--link d-link" href="#muted" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-stack direction="row" gap="400">
      <dt-split-button importance="outlined" kind="muted" omega-tooltip-text="More calling options"> Place Call </dt-split-button>
      <dt-split-button importance="clear" kind="muted" omega-tooltip-text="More calling options"> Place Call </dt-split-button>
  </dt-stack>
</code-well-header>
<code-example-tabs
htmlCode='
<span class="d-split-btn">
  <button class="base-button__button d-btn d-btn--outlined d-btn--muted d-split-btn__alpha d-split-btn__alpha--md" type="button">
    <span class="d-btn__label base-button__label"> Place Call </span>
  </button>
  <button class="base-button__button d-btn d-btn--outlined d-btn--muted d-btn--icon-only d-split-btn__omega d-split-btn__omega--md" type="button">
    <span class="base-button__icon d-btn__icon d-btn__icon--left">...</span>
  </button>
</span>
<span class="d-split-btn">
  <button class="base-button__button d-btn d-btn--muted d-split-btn__alpha d-split-btn__alpha--md" type="button">
    <span class="d-btn__label base-button__label"> Place Call </span>
  </button>
  <button class="base-button__button d-btn d-btn--muted d-btn--icon-only d-split-btn__omega d-split-btn__omega--md" type="button">
    <span class="base-button__icon d-btn__icon d-btn__icon--left">...</span>
  </button>
</span>
'
vueCode='
<dt-split-button importance="outlined" kind="muted" omega-tooltip-text="More calling options"> Place Call </dt-split-button>
<dt-split-button importance="clear" kind="muted" omega-tooltip-text="More calling options"> Place Call </dt-split-button>
'
showHtmlWarning />
<h3 id="disabled" tabindex="-1" class="d-docsite--header-3">Disabled <a class="header-anchor d-link d-docsite--link d-link" href="#disabled" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Use the <code v-pre>disabled</code> prop to disable both buttons, or use <code v-pre>alpha-disabled</code> and <code v-pre>omega-disabled</code> to disable each button independently.</p>
<code-well-header>
  <dt-stack direction="row" gap="400">
      <dt-split-button disabled omega-tooltip-text="More calling options"> Both disabled </dt-split-button>
      <dt-split-button alpha-disabled omega-tooltip-text="More calling options"> Alpha disabled </dt-split-button>
      <dt-split-button omega-disabled omega-tooltip-text="More calling options"> Omega disabled </dt-split-button>
  </dt-stack>
</code-well-header>
<code-example-tabs
htmlCode='
<span class="d-split-btn">
  <button class="base-button__button d-btn d-btn--primary d-split-btn__alpha d-split-btn__alpha--md" type="button" disabled>
    <span class="d-btn__label base-button__label"> Both disabled </span>
  </button>
  <button class="base-button__button d-btn d-btn--primary d-btn--icon-only d-split-btn__omega d-split-btn__omega--md" type="button" disabled>
    <span class="base-button__icon d-btn__icon d-btn__icon--left">...</span>
  </button>
</span>
<span class="d-split-btn">
  <button class="base-button__button d-btn d-btn--primary d-split-btn__alpha d-split-btn__alpha--md" type="button" disabled>
    <span class="d-btn__label base-button__label"> Alpha disabled </span>
  </button>
  <button class="base-button__button d-btn d-btn--primary d-btn--icon-only d-split-btn__omega d-split-btn__omega--md" type="button">
    <span class="base-button__icon d-btn__icon d-btn__icon--left">...</span>
  </button>
</span>
<span class="d-split-btn">
  <button class="base-button__button d-btn d-btn--primary d-split-btn__alpha d-split-btn__alpha--md" type="button">
    <span class="d-btn__label base-button__label"> Omega disabled </span>
  </button>
  <button class="base-button__button d-btn d-btn--primary d-btn--icon-only d-split-btn__omega d-split-btn__omega--md" type="button" disabled>
    <span class="base-button__icon d-btn__icon d-btn__icon--left">...</span>
  </button>
</span>
'
vueCode='
<dt-split-button disabled omega-tooltip-text="More calling options"> Both disabled </dt-split-button>
<dt-split-button alpha-disabled omega-tooltip-text="More calling options"> Alpha disabled </dt-split-button>
<dt-split-button omega-disabled omega-tooltip-text="More calling options"> Omega disabled </dt-split-button>
'
showHtmlWarning />
<h3 id="active" tabindex="-1" class="d-docsite--header-3">Active <a class="header-anchor d-link d-docsite--link d-link" href="#active" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-stack direction="row" gap="400">
    <dt-split-button alpha-active omega-tooltip-text="More calling options"> Alpha active </dt-split-button>
    <dt-split-button omega-active omega-tooltip-text="More calling options"> Omega active </dt-split-button>
  </dt-stack>
</code-well-header>
<code-example-tabs
htmlCode='
<span class="d-split-btn">
  <button class="base-button__button d-btn d-btn--primary d-btn--active d-split-btn__alpha d-split-btn__alpha--md" type="button">
    <span class="d-btn__label base-button__label"> Place Call </span>
  </button>
  <button class="base-button__button d-btn d-btn--primary d-btn--icon-only d-split-btn__omega d-split-btn__omega--md" type="button">
    <span class="base-button__icon d-btn__icon d-btn__icon--left">...</span>
  </button>
</span>
<span class="d-split-btn">
  <button class="base-button__button d-btn d-btn--primary d-split-btn__alpha d-split-btn__alpha--md" type="button">
    <span class="d-btn__label base-button__label"> Place Call </span>
  </button>
  <button class="base-button__button d-btn d-btn--primary d-btn--active d-btn--icon-only d-split-btn__omega d-split-btn__omega--md" type="button">
    <span class="base-button__icon d-btn__icon d-btn__icon--left">...</span>
  </button>
</span>
<span class="d-split-btn">
  <button class="base-button__button d-btn d-btn--primary d-btn--active d-split-btn__alpha d-split-btn__alpha--md" type="button">
    <span class="d-btn__label base-button__label"> Place Call </span>
  </button>
  <button class="base-button__button d-btn d-btn--primary d-btn--active d-btn--icon-only d-split-btn__omega d-split-btn__omega--md" type="button">
    <span class="base-button__icon d-btn__icon d-btn__icon--left">...</span>
  </button>
</span>
'
vueCode='
<dt-split-button alpha-active omega-tooltip-text="More calling options"> Alpha active </dt-split-button>
<dt-split-button omega-active omega-tooltip-text="More calling options"> Omega active </dt-split-button>
'
showHtmlWarning />
<h2 id="sizes" tabindex="-1" class="d-docsite--header-2">Sizes <a class="header-anchor d-link d-docsite--link d-link" href="#sizes" aria-hidden="true">#</a></h2>
<code-well-header>
  <dt-stack direction="row" gap="400">
    <dt-split-button size="xs" omega-tooltip-text="More calling options"> xs </dt-split-button>
    <dt-split-button size="sm" omega-tooltip-text="More calling options"> sm </dt-split-button>
    <dt-split-button size="md" omega-tooltip-text="More calling options"> md </dt-split-button>
    <dt-split-button size="lg" omega-tooltip-text="More calling options"> lg </dt-split-button>
    <dt-split-button size="xl" omega-tooltip-text="More calling options"> xl </dt-split-button>
  </dt-stack>
</code-well-header>
<code-example-tabs
htmlCode='
<span class="d-split-btn">
  <button class="base-button__button d-btn d-btn--primary d-btn--xs d-split-btn__alpha d-split-btn__alpha--xs" type="button">
    <span class="d-btn__label base-button__label"> Place Call </span>
  </button>
  <button class="base-button__button d-btn d-btn--primary d-btn--xs d-btn--icon-only d-split-btn__omega d-split-btn__omega--xs" type="button">
    <span class="base-button__icon d-btn__icon d-btn__icon--left">...</span>
  </button>
</span>
<span class="d-split-btn">
  <button class="base-button__button d-btn d-btn--primary d-btn--sm d-split-btn__alpha d-split-btn__alpha--sm" type="button">
    <span class="d-btn__label base-button__label"> Place Call </span>
  </button>
  <button class="base-button__button d-btn d-btn--primary d-btn--sm d-btn--icon-only d-split-btn__omega d-split-btn__omega--sm" type="button">
    <span class="base-button__icon d-btn__icon d-btn__icon--left">...</span>
  </button>
</span>
<span class="d-split-btn">
  <button class="base-button__button d-btn d-btn--primary d-split-btn__alpha d-split-btn__alpha--md" type="button">
    <span class="d-btn__label base-button__label"> Place Call </span>
  </button>
  <button class="base-button__button d-btn d-btn--primary d-btn--icon-only d-split-btn__omega d-split-btn__omega--md" type="button">
    <span class="base-button__icon d-btn__icon d-btn__icon--left">...</span>
  </button>
</span>
<span class="d-split-btn">
  <button class="base-button__button d-btn d-btn--primary d-btn--lg d-split-btn__alpha d-split-btn__alpha--lg" type="button">
    <span class="d-btn__label base-button__label"> Place Call </span>
  </button>
  <button class="base-button__button d-btn d-btn--primary d-btn--lg d-btn--icon-only d-split-btn__omega d-split-btn__omega--lg" type="button">
    <span class="base-button__icon d-btn__icon d-btn__icon--left">...</span>
  </button>
</span>
<span class="d-split-btn">
  <button class="base-button__button d-btn d-btn--primary d-btn--xl d-split-btn__alpha d-split-btn__alpha--xl" type="button">
    <span class="d-btn__label base-button__label"> Place Call </span>
  </button>
  <button class="base-button__button d-btn d-btn--primary d-btn--xl d-btn--icon-only d-split-btn__omega d-split-btn__omega--xl" type="button">
    <span class="base-button__icon d-btn__icon d-btn__icon--left">...</span>
  </button>
</span>
'
vueCode='
<dt-split-button size="xs" omega-tooltip-text="More calling options"> xs </dt-split-button>
<dt-split-button size="sm" omega-tooltip-text="More calling options"> sm </dt-split-button>
<dt-split-button size="md" omega-tooltip-text="More calling options"> md </dt-split-button>
<dt-split-button size="lg" omega-tooltip-text="More calling options"> lg </dt-split-button>
<dt-split-button size="xl" omega-tooltip-text="More calling options"> xl </dt-split-button>
'
showHtmlWarning />
<h2 id="loading" tabindex="-1" class="d-docsite--header-2">Loading <a class="header-anchor d-link d-docsite--link d-link" href="#loading" aria-hidden="true">#</a></h2>
<code-well-header>
  <dt-stack direction="row" gap="400">
    <dt-split-button alpha-loading omega-tooltip-text="More calling options"> Place call </dt-split-button>
    <dt-split-button alpha-loading importance="outlined" omega-tooltip-text="More calling options"> Place call </dt-split-button>
    <dt-split-button alpha-loading importance="clear" omega-tooltip-text="More calling options"> Place call </dt-split-button>
  </dt-stack>
</code-well-header>
<code-example-tabs
htmlCode='
<span class="d-split-btn">
  <button class="base-button__button d-btn d-btn--primary d-btn--loading d-split-btn__alpha d-split-btn__alpha--md" type="button">
    <span class="d-btn__label base-button__label"> Place Call </span>
  </button>
  <button class="base-button__button d-btn d-btn--primary d-btn--icon-only d-split-btn__omega d-split-btn__omega--md" type="button">
    <span class="base-button__icon d-btn__icon d-btn__icon--left">...</span>
  </button>
</span>
<span class="d-split-btn">
  <button class="base-button__button d-btn d-btn--outlined d-btn--loading d-split-btn__alpha d-split-btn__alpha--md" type="button">
    <span class="d-btn__label base-button__label"> Place Call </span>
  </button>
  <button class="base-button__button d-btn d-btn--outlined d-btn--icon-only d-split-btn__omega d-split-btn__omega--md" type="button">
    <span class="base-button__icon d-btn__icon d-btn__icon--left">...</span>
  </button>
</span>
<span class="d-split-btn">
  <button class="base-button__button d-btn d-btn--loading d-split-btn__alpha d-split-btn__alpha--md" type="button">
    <span class="d-btn__label base-button__label"> Place Call </span>
  </button>
  <button class="base-button__button d-btn d-btn--icon-only d-split-btn__omega d-split-btn__omega--md" type="button">
    <span class="base-button__icon d-btn__icon d-btn__icon--left">...</span>
  </button>
</span>
'
vueCode='
<dt-split-button alpha-loading omega-tooltip-text="More calling options"> Place call </dt-split-button>
<dt-split-button alpha-loading importance="outlined" omega-tooltip-text="More calling options"> Place call </dt-split-button>
<dt-split-button alpha-loading importance="clear" omega-tooltip-text="More calling options"> Place call </dt-split-button>
'
showHtmlWarning />
<h2 id="icon-support" tabindex="-1" class="d-docsite--header-2">Icon Support <a class="header-anchor d-link d-docsite--link d-link" href="#icon-support" aria-hidden="true">#</a></h2>
<h3 id="icon-and-label" tabindex="-1" class="d-docsite--header-3">Icon and Label <a class="header-anchor d-link d-docsite--link d-link" href="#icon-and-label" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-stack direction="row" gap="400">
    <dt-split-button importance="outlined" omega-tooltip-text="More calling options">
      <template #alphaIcon="{ size }">
        <dt-icon name="phone" :size="size" />
      </template>
      Place call
    </dt-split-button>
    <dt-split-button importance="outlined" alpha-icon-position="top" omega-tooltip-text="More calling options">
      <template #alphaIcon="{ size }">
        <dt-icon name="phone" :size="size" />
      </template>
      Place call
    </dt-split-button>
    <dt-split-button importance="outlined" alpha-icon-position="right" omega-tooltip-text="More calling options">
      <template #alphaIcon="{ size }">
        <dt-icon name="phone" :size="size" />
      </template>
      Place call
    </dt-split-button>
    <dt-split-button importance="outlined" alpha-icon-position="bottom" omega-tooltip-text="More calling options">
      <template #alphaIcon="{ size }">
        <dt-icon name="phone" :size="size" />
      </template>
      Place call
    </dt-split-button>
  </dt-stack>
</code-well-header>
<code-example-tabs
htmlCode='
<span class="d-split-btn">
  <button class="base-button__button d-btn d-btn--outlined d-split-btn__alpha d-split-btn__alpha--md" type="button">
    <span class="base-button__icon d-btn__icon d-btn__icon--left">
      <svg class="d-icon--size-300 d-icon">...</svg>
    </span>
    <span class="d-btn__label base-button__label"> Place Call </span>
  </button>
  <button class="base-button__button d-btn d-btn--outlined d-btn--icon-only d-split-btn__omega d-split-btn__omega--md" type="button">
    <span class="base-button__icon d-btn__icon d-btn__icon--left">...</span>
  </button>
</span>
<span class="d-split-btn">
  <button class="base-button__button d-btn d-btn--outlined d-split-btn__alpha d-split-btn__alpha--md" type="button">
    <span class="base-button__icon d-btn__icon d-btn__icon--top">
      <svg class="d-icon--size-300 d-icon">...</svg>
    </span>
    <span class="d-btn__label base-button__label"> Place Call </span>
  </button>
  <button class="base-button__button d-btn d-btn--outlined d-btn--icon-only d-split-btn__omega d-split-btn__omega--md" type="button">
    <span class="base-button__icon d-btn__icon d-btn__icon--left">...</span>
  </button>
</span>
<span class="d-split-btn">
  <button class="base-button__button d-btn d-btn--outlined d-split-btn__alpha d-split-btn__alpha--md" type="button">
    <span class="base-button__icon d-btn__icon d-btn__icon--right">
      <svg class="d-icon--size-300 d-icon">...</svg>
    </span>
    <span class="d-btn__label base-button__label"> Place Call </span>
  </button>
  <button class="base-button__button d-btn d-btn--outlined d-btn--icon-only d-split-btn__omega d-split-btn__omega--md" type="button">
    <span class="base-button__icon d-btn__icon d-btn__icon--left">...</span>
  </button>
</span>
<span class="d-split-btn">
  <button class="base-button__button d-btn d-btn--outlined d-split-btn__alpha d-split-btn__alpha--md" type="button">
    <span class="base-button__icon d-btn__icon d-btn__icon--bottom">
      <svg class="d-icon--size-300 d-icon">...</svg>
    </span>
    <span class="d-btn__label base-button__label"> Place Call </span>
  </button>
  <button class="base-button__button d-btn d-btn--outlined d-btn--icon-only d-split-btn__omega d-split-btn__omega--md" type="button">
    <span class="base-button__icon d-btn__icon d-btn__icon--left">...</span>
  </button>
</span>
'
vueCode='
<dt-split-button importance="outlined" omega-tooltip-text="More calling options">
  <template #alphaIcon="{ size }">
    <dt-icon name="phone" :size="size" />
  </template>
  Place call
</dt-split-button>
<dt-split-button importance="outlined" alpha-icon-position="top" omega-tooltip-text="More calling options">
  <template #alphaIcon="{ size }">
    <dt-icon name="phone" :size="size" />
  </template>
  Place call
</dt-split-button>
<dt-split-button importance="outlined" alpha-icon-position="right" omega-tooltip-text="More calling options">
  <template #alphaIcon="{ size }">
    <dt-icon name="phone" :size="size" />
  </template>
  Place call
</dt-split-button>
<dt-split-button importance="outlined" alpha-icon-position="bottom" omega-tooltip-text="More calling options">
  <template #alphaIcon="{ size }">
    <dt-icon name="phone" :size="size" />
  </template>
  Place call
</dt-split-button>
'
showHtmlWarning />
<h3 id="icon-only" tabindex="-1" class="d-docsite--header-3">Icon Only <a class="header-anchor d-link d-docsite--link d-link" href="#icon-only" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-stack direction="row" gap="400">
    <dt-split-button omega-tooltip-text="More calling options" alpha-tooltip-text="Place call">
      <template #alphaIcon="{ size }">
        <dt-icon name="phone" :size="size" />
      </template>
    </dt-split-button>
    <dt-split-button importance="outlined" kind="muted" omega-tooltip-text="More calling options" alpha-tooltip-text="Place call">
      <template #alphaIcon="{ size }">
        <dt-icon name="phone" :size="size" />
      </template>
    </dt-split-button>
    <dt-split-button importance="clear" kind="danger" omega-tooltip-text="More calling options" alpha-tooltip-text="Place call">
      <template #alphaIcon="{ size }">
        <dt-icon name="phone" :size="size" />
      </template>
    </dt-split-button>
  </dt-stack>
</code-well-header>
<code-example-tabs
htmlCode='
<span class="d-split-btn">
  <button class="base-button__button d-btn d-btn--primary d-split-btn__alpha d-split-btn__alpha--md" type="button">
    <span class="base-button__icon d-btn__icon d-btn__icon--left">
      <svg class="d-icon--size-300 d-icon">...</svg>
    </span>
  </button>
  <button class="base-button__button d-btn d-btn--outlined d-btn--icon-only d-split-btn__omega d-split-btn__omega--md" type="button">
    <span class="base-button__icon d-btn__icon d-btn__icon--left">...</span>
  </button>
</span>
<span class="d-split-btn">
  <button class="base-button__button d-btn d-btn--outlined d-split-btn__alpha d-split-btn__alpha--md" type="button">
    <span class="base-button__icon d-btn__icon d-btn__icon--left">
      <svg class="d-icon--size-300 d-icon">...</svg>
    </span>
  </button>
  <button class="base-button__button d-btn d-btn--outlined d-btn--icon-only d-split-btn__omega d-split-btn__omega--md" type="button">
    <span class="base-button__icon d-btn__icon d-btn__icon--left">...</span>
  </button>
</span>
<span class="d-split-btn">
  <button class="base-button__button d-btn d-btn--danger d-split-btn__alpha d-split-btn__alpha--md" type="button">
    <span class="base-button__icon d-btn__icon d-btn__icon--left">
      <svg class="d-icon--size-300 d-icon">...</svg>
    </span>
  </button>
  <button class="base-button__button d-btn d-btn--outlined d-btn--icon-only d-split-btn__omega d-split-btn__omega--md" type="button">
    <span class="base-button__icon d-btn__icon d-btn__icon--left">...</span>
  </button>
</span>
'
vueCode='
<dt-split-button omega-tooltip-text="More calling options" alpha-tooltip-text="Place call">
  <template #alphaIcon="{ size }">
    <dt-icon name="phone" :size="size" />
  </template>
</dt-split-button>
<dt-split-button importance="outlined" kind="muted" omega-tooltip-text="More calling options" alpha-tooltip-text="Place call">
  <template #alphaIcon="{ size }">
    <dt-icon name="phone" :size="size" />
  </template>
</dt-split-button>
<dt-split-button importance="clear" kind="danger" omega-tooltip-text="More calling options" alpha-tooltip-text="Place call">
  <template #alphaIcon="{ size }">
    <dt-icon name="phone" :size="size" />
  </template>
</dt-split-button>
'
showHtmlWarning />
<code-well-header bgclass="d-bgc-contrast">
  <dt-stack direction="row" gap="400">
    <dt-split-button kind="inverted" omega-tooltip-text="More calling options" alpha-tooltip-text="Place call">
      <template #alphaIcon="{ size }">
        <dt-icon name="phone" :size="size" />
      </template>
    </dt-split-button>
    <dt-split-button importance="outlined" kind="inverted" omega-tooltip-text="More calling options" alpha-tooltip-text="Place call">
      <template #alphaIcon="{ size }">
        <dt-icon name="phone" :size="size" />
      </template>
    </dt-split-button>
    <dt-split-button importance="clear" kind="inverted" omega-tooltip-text="More calling options" alpha-tooltip-text="Place call">
      <template #alphaIcon="{ size }">
        <dt-icon name="phone" :size="size" />
      </template>
    </dt-split-button>
  </dt-stack>
</code-well-header>
<code-example-tabs
htmlCode='
<span class="d-split-btn">
  <button class="base-button__button d-btn d-btn--primary d-btn--inverted d-split-btn__alpha d-split-btn__alpha--md" type="button">
    <span class="base-button__icon d-btn__icon d-btn__icon--left">
      <svg class="d-icon--size-300 d-icon">...</svg>
    </span>
  </button>
  <button class="base-button__button d-btn d-btn--primary d-btn--inverted d-btn--icon-only d-split-btn__omega d-split-btn__omega--md" type="button">
    <span class="base-button__icon d-btn__icon d-btn__icon--left">...</span>
  </button>
</span>
<span class="d-split-btn">
  <button class="base-button__button d-btn d-btn--outlined d-btn--inverted d-split-btn__alpha d-split-btn__alpha--md" type="button">
    <span class="base-button__icon d-btn__icon d-btn__icon--left">
      <svg class="d-icon--size-300 d-icon">...</svg>
    </span>
  </button>
  <button class="base-button__button d-btn d-btn--outlined d-btn--inverted d-btn--icon-only d-split-btn__omega d-split-btn__omega--md" type="button">
    <span class="base-button__icon d-btn__icon d-btn__icon--left">...</span>
  </button>
</span>
<span class="d-split-btn">
  <button class="base-button__button d-btn d-btn--inverted d-split-btn__alpha d-split-btn__alpha--md" type="button">
    <span class="base-button__icon d-btn__icon d-btn__icon--left">
      <svg class="d-icon--size-300 d-icon">...</svg>
    </span>
  </button>
  <button class="base-button__button d-btn d-btn--inverted d-btn--icon-only d-split-btn__omega d-split-btn__omega--md" type="button">
    <span class="base-button__icon d-btn__icon d-btn__icon--left">...</span>
  </button>
</span>
'
vueCode='
<dt-split-button kind="inverted" omega-tooltip-text="More calling options" alpha-tooltip-text="Place call">
  <template #alphaIcon="{ size }">
    <dt-icon name="phone" :size="size" />
  </template>
</dt-split-button>
<dt-split-button importance="outlined" kind="inverted" omega-tooltip-text="More calling options" alpha-tooltip-text="Place call">
  <template #alphaIcon="{ size }">
    <dt-icon name="phone" :size="size" />
  </template>
</dt-split-button>
<dt-split-button importance="clear" kind="inverted" omega-tooltip-text="More calling options" alpha-tooltip-text="Place call">
  <template #alphaIcon="{ size }">
    <dt-icon name="phone" :size="size" />
  </template>
</dt-split-button>
'
showHtmlWarning />
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="splitButton" /><h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">In addition to the <RouteLink to="/components/button.html#accessibility" class="d-docsite--link d-link">Button component's</RouteLink> accessibility documentation:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">An icon-only primary action and the secondary action require a <RouteLink to="/components/tooltip.html" class="d-docsite--link d-link">Tooltip</RouteLink> to indicate its function.</li>
<li class="d-docsite--list-element">While the secondary action is focused, pressing <code v-pre>Enter</code> or <code v-pre>Space</code> triggers its action, displaying a <RouteLink to="/components/dropdown.html" class="d-docsite--link d-link">Dropdown component</RouteLink> or <RouteLink to="/components/popover.html" class="d-docsite--link d-link">Popover component</RouteLink>.</li>
</ul>
<h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<component-class-table component-name="split-button" /></div></template>


